import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

export interface CompanyOffice {
  id: string;
  name: string;
  isHeadOffice?: boolean;
}

export function useCompanyOffices(companyId: string | null) {
  const [offices, setOffices] = useState<CompanyOffice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) {
      setOffices([]);
      setLoading(false);
      return;
    }

    let mounted = true;
    const fetchOffices = async () => {
      setLoading(true);
      try {
        const snap = await getDocs(collection(db, 'companies', companyId, 'offices'));
        if (!mounted) return;
        const list: CompanyOffice[] = snap.docs.map((d) => ({
          id: d.id,
          name: d.data().name || 'Unnamed Office',
          isHeadOffice: d.data().isHeadOffice || false,
        }));
        // Head office first, then alphabetical
        list.sort((a, b) => {
          if (a.isHeadOffice && !b.isHeadOffice) return -1;
          if (!a.isHeadOffice && b.isHeadOffice) return 1;
          return a.name.localeCompare(b.name);
        });
        setOffices(list);
      } catch (err) {
        console.error('[useCompanyOffices] Failed to fetch:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchOffices();
    return () => { mounted = false; };
  }, [companyId]);

  const createOffice = useCallback(async (name: string): Promise<CompanyOffice> => {
    if (!companyId) throw new Error('No companyId');
    const ref = await addDoc(collection(db, 'companies', companyId, 'offices'), {
      name,
      isHeadOffice: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    const newOffice: CompanyOffice = { id: ref.id, name, isHeadOffice: false };
    setOffices((prev) => [...prev, newOffice]);
    return newOffice;
  }, [companyId]);

  return { offices, loading, createOffice };
}
